<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { customValidators } from "@/i18n/utils/i18n-validators";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import type { Schemas } from "#shopware";
import { useTemplateRef } from "vue";

const { required, minLength, requiredIf } = customValidators();
const { resolveApiErrors } = useApiErrorsResolver("account_login");
const { createCustomerAddress, updateCustomerAddress, errorMessageBuilder } =
  useAddress();
const { getStatesForCountry } = useCountries();



const emits = defineEmits<{
  (e: "success", addressId?: string): void;
  (e: "close"): void;
  (e: "switchToSelection"): void;
}>();

const props = withDefaults(
  defineProps<{
    address?: Schemas["CustomerAddress"];
    title?: string;
    hideBackToSelection?: boolean;
    makeActiveAddress?: boolean;
  }>(),
  {
    title: "Account address",
    address: undefined,
  }
);

const { getSalutations } = useSalutations();
const { t } = useI18n();
const { pushError, pushSuccess } = useNotifications();

const formData = reactive({
  countryId: props.address?.countryId ?? "",
  salutationId: props.address?.salutationId ?? "",
  firstName: props.address?.firstName ?? "",
  lastName: props.address?.lastName ?? "",
  company: props.address?.company ?? "",
  department: props.address?.department ?? "",
  zipcode: props.address?.zipcode ?? "",
  city: props.address?.city ?? "",
  street: props.address?.street ?? "",
  phoneNumber: props.address?.phoneNumber ?? "",
  customFields: {
    custom_address_field_phone_number_2:
      props.address?.customFields?.custom_address_field_phone_number_2 ?? "",
  },
  id: props.address?.id ?? "",
});

const loadingData = ref(false);

const errors = ref([]);
const invokeSave = async (): Promise<void> => {
  if (props.makeActiveAddress) {
    formData.isDefaultShippingAddress = true;
  }
  try {
    loadingData.value = true;
    const saveAddress = formData.id
      ? updateCustomerAddress
      : createCustomerAddress;

    $v.value.$touch();
    const valid = await $v.value.$validate();
    if (valid) {
      const result = await saveAddress(formData as Schemas["CustomerAddress"]);
      const addessId = result?.id;
      pushSuccess(t("account.messages.addressSavedSuccess"));
      if(formData.id) {
        emits("success");
      } else {
        emits("success", addessId);
      }
    }
  } catch (error) {
    // if (error instanceof ApiClientError) {
      errors.value = resolveApiErrors(error.details.errors as ApiError[]);
  } finally {
    loadingData.value = false;
  }
};

const firstNameInputElement = useTemplateRef("firstNameInputElement");
useFocus(firstNameInputElement, { initialValue: true });

const rules = computed(() => ({
  salutationId: {
    required,
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  company: {
    // optional
  },
  department: {
    // optional
  },
  zipcode: {
    required,
  },
  street: {
    required,
    minLength: minLength(3),
  },
  city: {
    required,
  },
  countryId: {
    required,
  },
  phoneNumber: {
    required,
    minLength: minLength(6),
  },
  customFields: {
    custom_address_field_phone_number_2: {},
  },
}));

const $v = useVuelidate(rules, formData);

const handleSwitchToSelection = () => {
  emits("switchToSelection");
};

</script>

<template>
  <div class="min-h-full flex flex-col p-0 md:p-6">
    <div class="max-w-2xl w-full space-y-8">
      <div>
        <h2 class="text-start text-lg md:text-xl font-medium text-primary mb-4">
          {{ props.title }}
        </h2>
      </div>
      
      <form class="mt-6 space-y-6" @submit.prevent="invokeSave">
        <div class="rounded-md space-y-6">
          <!-- Salutation -->
          <div class="relative">
            <select
              id="salutation"
              v-model="formData.salutationId"
              required
              name="salutation"
              aria-label="Customer salutation"
              autocomplete="off"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
              :class="[
                $v.salutationId.$error
                  ? 'border-red focus:border-red'
                  : 'border-secondary-300',
              ]"
              @blur="$v.salutationId.$touch()"
              data-testid="account-address-form-salutation-select"
            >
              <option value="" disabled>{{ $t("form.salutation") }}</option>
              <option
                v-for="salutation in getSalutations"
                :key="salutation.id"
                :value="salutation.id"
                data-testid="account-address-form-salutation-select-option"
              >
                {{ salutation.displayName }}
              </option>
            </select>
            <label
              for="salutation"
              class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
              :class="{
                '-translate-y-5 text-xs px-2 bg-white': formData.salutationId || $v.salutationId.$error,
                'text-red': $v.salutationId.$error,
                'text-primary': !$v.salutationId.$error && (formData.salutationId || $v.salutationId.$dirty),
              }"
            >
              {{ $t("form.salutation") }}
            </label>
            <span v-if="$v.salutationId.$error" class="block mt-1 text-xs text-red">
              {{ $v.salutationId.$errors[0].$message }}
            </span>
          </div>

          <!-- Name Fields -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="relative">
              <input
                id="first-name"
                ref="firstNameInputElement"
                v-model="formData.firstName"
                type="text"
                required
                name="first-name"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
                :class="[
                  $v.firstName.$error
                    ? 'border-red focus:border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.firstName.$touch()"
                data-testid="account-address-form-firstname-input"
              />
              <label
                for="first-name"
                class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs px-2 bg-white': formData.firstName || $v.firstName.$error,
                  'text-red': $v.firstName.$error,
                  'text-primary': !$v.firstName.$error && (formData.firstName || $v.firstName.$dirty),
                }"
              >
                {{ $t("form.firstName") }}
              </label>
              <span v-if="$v.firstName.$error" class="block mt-1 text-xs text-red">
                {{ $v.firstName.$errors[0].$message }}
              </span>
            </div>

            <div class="relative">
              <input
                id="last-name"
                v-model="formData.lastName"
                type="text"
                required
                name="last-name"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
                :class="[
                  $v.lastName.$error
                    ? 'border-red focus:border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.lastName.$touch()"
                data-testid="account-address-form-lastname-input"
              />
              <label
                for="last-name"
                class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs px-2 bg-white': formData.lastName || $v.lastName.$error,
                  'text-red': $v.lastName.$error,
                  'text-primary': !$v.lastName.$error && (formData.lastName || $v.lastName.$dirty),
                }"
              >
                {{ $t("form.lastName") }}
              </label>
              <span v-if="$v.lastName.$error" class="block mt-1 text-xs text-red">
                {{ $v.lastName.$errors[0].$message }}
              </span>
            </div>
          </div>

          

          <!-- Company & Department -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="relative">
              <input
                id="company"
                v-model="formData.company"
                type="text"
                name="company"
                autocomplete="organization"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
                :class="[
                  $v.company?.$error
                    ? 'border-red focus:border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.company?.$touch()"
              />
              <label
                for="company"
                class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs px-2 bg-white': formData.company || $v.company?.$error,
                  'text-red': $v.company?.$error,
                  'text-primary':
                    !$v.company?.$error &&
                    (formData.company || $v.company?.$dirty),
                }"
              >
                {{ $t("form.company") }}
              </label>
            </div>

            <div class="relative">
              <input
                id="department"
                v-model="formData.department"
                type="text"
                name="department"
                autocomplete="organization-title"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
                :class="[
                  $v.department.$error
                    ? 'border-red focus:border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.department.$touch()"
              />
              <label
                for="department"
                class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs px-2 bg-white': formData.department || $v.department.$error,
                  'text-red': $v.department.$error,
                  'text-primary':
                    !$v.department.$error &&
                    (formData.department || $v.department.$dirty),
                }"
              >
                {{ $t("form.departmentPlaceholder") }}
              </label>
              <span v-if="$v.department.$error" class="block mt-1 text-xs text-red">
                {{ $v.department.$errors[0].$message }}
              </span>
            </div>
          </div>

          <!-- Street Address -->
          <div class="relative">
            <input
              id="street-address"
              v-model="formData.street"
              type="text"
              required
              name="street-address"
              autocomplete="street-address"
              class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
              :class="[
                $v.street.$error
                  ? 'border-red focus:border-red'
                  : 'border-secondary-300',
              ]"
              @blur="$v.street.$touch()"
              data-testid="account-address-form-street-input"
            />
            <label
              for="street-address"
              class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
              :class="{
                '-translate-y-5 text-xs px-2 bg-white': formData.street || $v.street.$error,
                'text-red': $v.street.$error,
                'text-primary': !$v.street.$error && (formData.street || $v.street.$dirty),
              }"
            >
              {{ $t("form.streetAddress") }}
            </label>
            <span v-if="$v.street.$error" class="block mt-1 text-xs text-red">
              {{ $v.street.$errors[0].$message }}
            </span>
          </div>

          <!-- Country and State -->
          <SharedCountryStateInput
            v-model:country-id="formData.countryId"
            :country-id-validation="$v.countryId"
            class="w-full"
          />
          <!-- City and Postal Code -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div class="sm:col-span-2 relative">
              <input
                id="city"
                v-model="formData.city"
                type="text"
                required
                name="city"
                autocomplete="address-level2"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
                :class="[
                  $v.city.$error
                    ? 'border-red focus:border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.city.$touch()"
              />
              <label
                for="city"
                class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs px-2 bg-white': formData.city || $v.city.$error,
                  'text-red': $v.city.$error,
                  'text-primary': !$v.city.$error && (formData.city || $v.city.$dirty),
                }"
              >
                {{ $t("form.city") }}
              </label>
              <span v-if="$v.city.$error" class="block mt-1 text-xs text-red">
                {{ $v.city.$errors[0].$message }}
              </span>
            </div>
            
            <div class="relative">
              <input
                id="postal-code"
                v-model="formData.zipcode"
                type="text"
                required
                name="postal-code"
                autocomplete="postal-code"
                class="appearance-none relative block w-full px-3 py-3 border placeholder-transparent text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-base peer"
                :class="[
                  $v.zipcode.$error
                    ? 'border-red focus:border-red'
                    : 'border-secondary-300',
                ]"
                @blur="$v.zipcode.$touch()"
                data-testid="account-address-form-postal-code-input"
              />
              <label
                for="postal-code"
                class="absolute left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs px-2 bg-white': formData.zipcode || $v.zipcode.$error,
                  'text-red': $v.zipcode.$error,
                  'text-primary': !$v.zipcode.$error && (formData.zipcode || $v.zipcode.$dirty),
                }"
              >
                {{ $t("form.postalCode") }}
              </label>
              <span v-if="$v.zipcode.$error" class="block mt-1 text-xs text-red">
                {{ $v.zipcode.$errors[0].$message }}
              </span>
            </div>
          </div>

          <!-- Phone Numbers -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="relative">
              <ClientOnly>
                <SharedIntlTelInput
                    v-model="formData.phoneNumber"
                    :label="$t('form.phone1Placeholder')"
                    :required="true"
                    :error="$v.phoneNumber.$error ? $v.phoneNumber.$errors[0].$message : ''"
                    id="phone-number"
                    name="phone-number"
                    type="tel"
                    @blur="$v.phoneNumber.$touch()"
                    data-testid="account-address-form-phone-input"
                    :placeholder="$t('form.phone1Placeholder')"
                  />
              </ClientOnly>
              <label
                for="phone-number"
                class="absolute z-[10] pl-14 peer-focus:pl-0 left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs pl-2 pr-2 bg-white': formData.phoneNumber || $v.phoneNumber.$error,
                  'text-red': $v.phoneNumber.$error,
                  'text-primary': !$v.phoneNumber.$error && (formData.phoneNumber || $v.phoneNumber.$dirty),
                }"
              >
                {{ $t("form.phone1Placeholder") }}
              </label>
              <span v-if="$v.phoneNumber.$error" class="block mt-1 text-xs text-red">
                {{ $v.phoneNumber.$errors[0].$message }}
              </span>
            </div>

            <div class="relative">
              <SharedIntlTelInput
                v-model="formData.customFields.custom_address_field_phone_number_2"
                :label="$t('form.phone2Placeholder')"
                :required="false"
                id="phone-number-2"
                name="phone-number-2"
                :placeholder="$t('form.phone2Placeholder')"
                type="tel"
                @blur="$v.phoneNumber.$touch()"
                data-testid="account-address-form-phone-input"
              />
              <label
                for="phone-number-2"
                class="absolute pl-14 peer-focus:pl-0 left-3 top-3 text-secondary-500 transition-all duration-200 pointer-events-none peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-primary peer-focus:px-2 peer-focus:bg-white"
                :class="{
                  '-translate-y-5 text-xs pl-2 pr-2 bg-white': formData.customFields.custom_address_field_phone_number_2,
                  'text-primary': formData.customFields.custom_address_field_phone_number_2,
                }"
              >
                {{ $t("form.phone2Placeholder") }}
              </label>
            </div>
          </div>
          <p class="text-sm text-gray-500 -mt-2">
            {{ $t("checkout.registration.phoneNote") }}
          </p>
        </div>

        <div v-if="errors?.length" class="text-red text-sm">
          <ul>
            <li v-for="error in errors" :key="error.detail">
              {{ error }}
            </li>
          </ul>
        </div>

        <!-- Submit Button -->
        <div class="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="submit"
            class="btn btn-primary justify-center flex-shrink-1"
            data-testid="account-address-form-submit-button"
            :disabled="loadingData"
            :class="{
              'w-full md:w-[50%]': !props.address?.id && !props.hideBackToSelection,
              'w-full': props.address?.id || props.hideBackToSelection,
            }"
          >
            <span class="my-auto">
              {{ $t("form.save") }}
            </span>
            <span v-if="loadingData" class="ml-2">
              <i class="fa-solid fa-spinner animate-spin"></i>
            </span>
          </button>
          
          <!-- Only show "Back to Address Selection" button for new addresses -->
          <button
            v-if="!props.address?.id && !props.hideBackToSelection"
            type="button"
            class="btn btn-secondary w-full md:w-[50%] justify-center flex-shrink-0"
            @click="handleSwitchToSelection"
          >
            {{ $t("checkout.backToAddressSelection") }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
